import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { VerifyStatus } from '../verify-badge';
import { VerifyBadge } from '../verify-badge';

import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

type ConfigResponse = InferResponseType<(typeof api.api.org)[':orgId']['integrations']['$get']>;

interface StagesResponse {
  stages: Array<{ id: string; name: string }>;
}

interface TeamtailorFormProps {
  orgId: string;
  config: NonNullable<ConfigResponse['config']> | null;
}

const STAGE_FIELDS = [
  'initialStage',
  'passInterviewStage',
  'failInterviewStage',
  'recommendedStage'
] as const;

type StageMappingKey = (typeof STAGE_FIELDS)[number];

export function TeamtailorForm({ orgId, config }: TeamtailorFormProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [apiKey, setApiKey] = useState('');
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>('idle');
  const [stageMapping, setStageMapping] = useState<Record<StageMappingKey, string>>({
    initialStage: config?.stageMapping.initialStage ?? '',
    passInterviewStage: config?.stageMapping.passInterviewStage ?? '',
    failInterviewStage: config?.stageMapping.failInterviewStage ?? '',
    recommendedStage: config?.stageMapping.recommendedStage ?? ''
  });

  const hasApiKey = config?.hasApiKey ?? false;
  const hasConnection = hasApiKey || apiKey.length > 0;

  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ['integrations', 'stages', orgId],
    enabled: hasApiKey,
    queryFn: async () => {
      const res = await api.api.org[':orgId'].integrations.stages.$get({ param: { orgId } });
      if (!res.ok) throw new Error(i18n.t('integrations.errors.fetchStagesFailed'));
      return res.json() as Promise<StagesResponse>;
    }
  });

  const stages = stagesData?.stages ?? [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!hasApiKey && apiKey.length === 0) throw new Error('API key required');
      const payload = {
        provider: 'teamtailor' as const,
        ...(apiKey.length > 0 && { apiKey }),
        stageMapping
      };
      const res = await api.api.org[':orgId'].integrations.$put({
        param: { orgId },
        json: payload
      });
      if (!res.ok) throw new Error(i18n.t('integrations.errors.saveFailed'));
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'config', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'stages', orgId] });
      setApiKey('');
    }
  });

  async function handleVerify(): Promise<void> {
    if (!hasConnection) return;
    setVerifyStatus('loading');
    try {
      const query = apiKey.length > 0 ? { provider: 'teamtailor' as const, apiKey } : {};
      const res = await api.api.org[':orgId'].integrations.verify.$get({
        param: { orgId },
        query
      });
      if (!res.ok) {
        setVerifyStatus('error');
        return;
      }
      const data = await res.json();
      setVerifyStatus(data.status as VerifyStatus);
    } catch {
      setVerifyStatus('error');
    }
  }

  const verifyLabel = t(`integrations.ats.verifyStatus.${verifyStatus}`);

  return (
    <div className="flex flex-col gap-6">
      {/* Connection section */}
      <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
        <h2 className="text-base font-semibold">{t('integrations.ats.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('integrations.ats.description')}</p>

        <div className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">
              {t('integrations.ats.apiKey')}
            </label>
            {hasApiKey && apiKey.length === 0 && (
              <p className="text-xs text-muted-foreground">{t('integrations.ats.apiKeySet')}</p>
            )}
            <Input
              type="password"
              placeholder={hasApiKey ? '••••••••••••••••' : t('integrations.ats.apiKeyPlaceholder')}
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
              }}
              className="max-w-sm"
            />
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void handleVerify();
              }}
              disabled={!hasConnection || verifyStatus === 'loading'}
            >
              {t('integrations.ats.verify')}
            </Button>
            {verifyStatus !== 'idle' && <VerifyBadge status={verifyStatus} label={verifyLabel} />}
          </div>
        </div>
      </div>

      {/* Stage mapping section — only show once connection is established */}
      {hasApiKey && (
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <h2 className="text-base font-semibold">{t('integrations.stageMapping.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('integrations.stageMapping.description')}
          </p>

          {stagesLoading ? (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {STAGE_FIELDS.map((field) => (
                <div key={field} className="flex flex-col gap-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {STAGE_FIELDS.map((field) => (
                <div key={field} className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-foreground">
                    {t(`integrations.stageMapping.${field}`)}
                  </label>
                  <Select
                    value={stageMapping[field]}
                    onValueChange={(value) => {
                      setStageMapping((prev) => ({ ...prev, [field]: value }));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('integrations.stageMapping.selectStage')} />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage: { id: string; name: string }) => (
                        <SelectItem key={stage.id} value={stage.name}>
                          {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => {
            saveMutation.mutate();
          }}
          disabled={saveMutation.isPending || (!hasApiKey && apiKey.length === 0)}
        >
          {saveMutation.isPending ? '...' : t('integrations.ats.save')}
        </Button>
      </div>
    </div>
  );
}
